<script lang="ts">
export default { name: 'SchoolItem' }
</script>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import { Common } from '@/components'
import SchoolItemCheckbox from './school-item-checkbox.vue'

const props = defineProps({
  // コンポーネント特有のプロパティ

  /**
   * コンポーネントのタイプ
   *
   * * null(初期値): 普通の一覧用
   * * checkable: チェックボックスで複数選択可能
   * * removable: 削除ボタンを表示
   */
  type: { type: String as PropType<'checkable' | 'removable'>, default: null },

  /**
   * 学校IDと請求IDのをつなげた文字列の配列
   *
   * e.g. `['D100001_100001', 'T300001_300001']`
   */
  checked: { type: Array as PropType<string[]> },

  // 学校の情報
  keitou: { type: Object as PropType<{ [key: number]: string }> },
  photo: { type: String, required: true },
  prefecture: { type: String, required: true },
  prefectureName: { type: String, required: true },
  ryokin: { type: String, required: true },
  schoolDataId: { type: Object as PropType<string | null>, required: false, default: null },
  schoolName: { type: String, required: true },
  schoolNo: { type: String, required: true },
  schoolType: { type: Number, required: true },
  schoolTypeName: { type: String, required: true },
  seikyuuCode: { type: String, required: true },
  shiryouType: { type: Number, required: true },
})

const emit = defineEmits(['update:checked', 'remove'])

const innerChecked = computed(() => {
  if (props.type !== 'checkable') return false

  const set = new Set(props.checked)
  return set.has(`${props.schoolNo}_${props.seikyuuCode}`)
})

const waiAria = computed(() => {
  if (props.type === 'checkable') {
    return {
      tabindex: '0',
      role: 'checkbox',
      ariaChecked: innerChecked.value,
    }
  } else {
    return undefined
  }
})

function onToggle() {
  if (props.type !== 'checkable') return

  const set = new Set(props.checked)
  const value = `${props.schoolNo}_${props.seikyuuCode}`
  if (set.has(value)) {
    set.delete(value)
  } else {
    set.add(value)
  }
  emit('update:checked', [...set])
}

function onRemove() {
  emit('remove')
}

const wrapperClass = computed(() => {
  const classes: (string | { [key: string]: boolean })[] = []

  // クリッカブル
  if (props.type === 'checkable') {
    classes.push(
      'cursor-pointer',
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-orange',
      'focus-visible:outline-offset-1',
    )
  }

  // 背景と枠線
  if (props.type === 'checkable' && !innerChecked.value) {
    classes.push('bg-white', 'border-[#B5B5B5]')
  } else {
    classes.push('bg-[#FFF9E5]', 'border-[#F39111]')
  }

  return classes
})

/** 有料か否か */
const isPaid = computed(() => props.ryokin !== '送料・資料とも無料！')
</script>

<template>
  <div>
    <div
      class="flex items-center py-2 px-2 sm:px-4 border-2 rounded"
      :class="wrapperClass"
      v-bind="waiAria"
      @click="onToggle"
    >
      <SchoolItemCheckbox v-if="type === 'checkable'" class="shrink-0 mr-2 sm:mr-3" :checked="innerChecked" />

      <div class="flex-grow grid grid-cols-[100px_1fr] sm:grid-cols-[136px_1fr] grid-flow-row-dense gap-2 sm:gap-3">
        <div
          class="col-span-2 sm:col-start-2 sm:col-end-3 text-xl font-bold border-b border-dashed border-[#B5B5B5] pb-1"
        >
          {{ schoolName }}
        </div>

        <div class="row-start-2 sm:row-span-2">
          <img
            :src="photo"
            width="120"
            height="80"
            class="w-[100px] h-[75px] sm:w-[136px] sm:h-[102px] object-cover"
            alt=""
          />
        </div>

        <div class="flex flex-col sm:flex-row gap-2">
          <div class="grow flex flex-col gap-1">
            <div v-if="keitou" class="flex flex-wrap gap-1">
              <template v-for="[id, name] of Object.entries(keitou)" :key="`keitou_${id}`">
                <span class="p-0.5 border border-[#F39111] rounded-sm bg-[#fcecdd] text-xs">{{ name }}</span>
              </template>
            </div>

            <div class="text-gray-500 text-xs sm:text-sm">
              <span> {{ prefectureName }}</span> / <span> {{ schoolTypeName }}</span> /
              <span :class="{ 'text-red-500 font-bold': isPaid }">{{ ryokin }}</span>
            </div>
          </div>

          <div v-if="type === 'removable'" class="self-end">
            <button @click="onRemove" class="btn-outline text-xs sm:text-sm px-3 py-2 whitespace-nowrap">
              カートから削除する
              <Common.Icon name="trash" class="ml-1 text-[1.1rem]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
